import type { BrowserInfo, Collector } from "./types";
import { isBrowser } from "./types";

/**
 * Browser detection — must run before audio/storage collectors so they can
 * branch on isBrave / isSafari / isIosChrome.
 *
 * Brave's UA does NOT contain "brave" (it masquerades as Chrome to avoid
 * breaking sites). The async navigator.brave.isBrave() API is authoritative;
 * the UA-substring fallback is a last resort.
 */
export const browserCollector: Collector<BrowserInfo> = {
  name: "browser",
  version: 2,
  timeoutMs: 500,
  async collect() {
    if (!isBrowser) {
      return {
        isBrave: false,
        isSafari: false,
        isFirefox: false,
        isIosChrome: false,
        ua: "",
        brands: [] as string[],
        family: "unknown",
        version: "",
        engine: "unknown",
      };
    }

    const ua = navigator.userAgent || "";
    const uaData: any = (navigator as any).userAgentData;
    const brands = uaData?.brands?.map((b: any) => b.brand) || [];

    let isBrave = false;
    try {
      const nb: any = (navigator as any).brave;
      isBrave = !!(nb && (await nb.isBrave?.()));
    } catch {
      /* ignore */
    }
    if (!isBrave) {
      isBrave = ua.includes("Brave") || brands.some((b: string) => /Brave/i.test(b));
    }

    const { isSafari, isFirefox, isIosChrome } = detectPrivacyEngineFlags(ua, uaData);

    let family = "unknown";
    let version = "";
    let engine = "unknown";

    if (ua) {
      if (isBrave) {
        family = "Brave";
        engine = "Blink";
        version = extractBraveVersion(ua);
      } else if (isIosChrome) {
        family = "Chrome";
        engine = "WebKit";
        version = extractCriOSVersion(ua);
      } else if (isSafari) {
        family = "Safari";
        engine = "WebKit";
        version = extractSafariVersion(ua);
      } else if (isFirefox) {
        family = "Firefox";
        engine = "Gecko";
        version = extractFirefoxVersion(ua);
      } else if (ua.includes("Edge")) {
        family = "Edge";
        engine = "Blink";
        version = extractEdgeVersion(ua);
      } else if (ua.includes("Opera")) {
        family = "Opera";
        engine = "Blink";
        version = extractOperaVersion(ua);
      } else if (ua.includes("Chrome") || ua.includes("Chromium")) {
        family = "Chrome";
        engine = "Blink";
        version = extractChromeVersion(ua);
      } else {
        family = "Other";
      }
    }

    return { isBrave, isSafari, isFirefox, isIosChrome, ua, brands, family, version, engine };
  },
};

/**
 * Pure-function detection of privacy-engine flags (Safari / Firefox / iOS-Chrome),
 * extracted from `browserCollector` so it's unit-testable without jsdom.
 *
 * Robust to DevTools UA spoofing: gates each UA-string match on the absence of
 * `navigator.userAgentData` (a Chromium-only API that Apple/Mozilla deliberately
 * do not ship; DevTools mobile emulation changes the UA string but leaves
 * userAgentData intact). Without this gate, DevTools-emulating-iPhone in Chrome
 * misfires `isSafari=true`, which the server's v6 privacy-branch then uses to
 * strip audio/fonts/hw.cores from `device_core_id` — breaking stability across
 * the emulation toggle (the Phase 8 / req #2 invariant).
 *
 * Brave detection stays in the async collector body — it uses `navigator.brave`,
 * not UA strings, so it's already spoofing-resistant.
 */
export function detectPrivacyEngineFlags(
  ua: string,
  uaData: { brands?: { brand: string }[] } | undefined,
): { isSafari: boolean; isFirefox: boolean; isIosChrome: boolean } {
  const hasUaCh = !!(uaData && Array.isArray(uaData.brands) && uaData.brands.length > 0);
  const isIosChrome = !hasUaCh && /\bCriOS\//i.test(ua);
  const isSafari =
    !hasUaCh &&
    ua.includes("Safari") &&
    !ua.includes("Chrome") &&
    !ua.includes("Chromium") &&
    !isIosChrome;
  const isFirefox = !hasUaCh && ua.includes("Firefox") && !ua.includes("Seamonkey");
  return { isSafari, isFirefox, isIosChrome };
}

export function extractBraveVersion(ua: string): string {
  const match = ua.match(/Chrome\/([0-9]+\.[0-9]+\.[0-9]+\.[0-9]+)/);
  return match ? match[1] : "";
}

export function extractSafariVersion(ua: string): string {
  const match = ua.match(/Version\/([0-9]+\.[0-9]+(?:\.[0-9]+)?)/);
  return match ? match[1] : "";
}

export function extractFirefoxVersion(ua: string): string {
  const match = ua.match(/Firefox\/([0-9]+\.[0-9]+(?:\.[0-9]+)?)/);
  return match ? match[1] : "";
}

export function extractEdgeVersion(ua: string): string {
  const match = ua.match(/Edg\/([0-9]+\.[0-9]+\.[0-9]+\.[0-9]+)/);
  return match ? match[1] : "";
}

export function extractOperaVersion(ua: string): string {
  const match = ua.match(/OPR\/([0-9]+\.[0-9]+\.[0-9]+\.[0-9]+)/);
  return match ? match[1] : "";
}

export function extractChromeVersion(ua: string): string {
  const match = ua.match(/Chrome\/([0-9]+\.[0-9]+\.[0-9]+\.[0-9]+)/);
  return match ? match[1] : "";
}

export function extractCriOSVersion(ua: string): string {
  const match = ua.match(/CriOS\/([\d.]+)/i);
  return match ? match[1] : "";
}

export function parseChromiumMajorForTelemetry(version: string, family: string): number | null {
  if (!version || !["Chrome", "Edge", "Opera"].includes(family)) return null;
  const m = version.match(/^(\d+)/);
  return m ? parseInt(m[1], 10) : null;
}

/**
 * Stable browser core identifier — version-agnostic. `isBrave` MUST be
 * passed in (Brave's UA masquerades as Chrome; UA-only matching mis-classifies
 * Brave as chromium-core and erases the device_core_id distinction).
 */
export function extractBrowserCore(userAgent: string, isBrave: boolean): string {
  if (isBrave) return "brave-core";
  const ua = userAgent.toLowerCase();
  if (ua.includes("edg/") || ua.includes("edge/")) return "edge-core";
  if (ua.includes("opr/") || ua.includes("opera")) return "opera-core";
  if (ua.includes("firefox") && !ua.includes("seamonkey")) return "gecko-core";
  if (ua.includes("safari") && !ua.includes("chrome") && !ua.includes("chromium"))
    return "webkit-core";
  if (ua.includes("chrome") || ua.includes("chromium")) return "chromium-core";
  return "unknown-core";
}
